// Thin fetch wrapper around the FastAPI backend. Cookies (session) are sent
// via credentials: 'include'. In dev, Vite proxies /api → :8000 (see vite.config.ts).

export interface ApiError {
  status: number;
  detail: string;
  // Machine-readable error code. Set by the backend on gate failures so the
  // UI can branch (e.g. show an "upgrade to Pro" prompt instead of a raw
  // error). Values: 'rights_not_acknowledged' | 'anon_quota_exceeded' |
  // 'free_quota_exceeded' | undefined.
  code?: string;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  init?: RequestInit
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body instanceof FormData
      ? undefined
      : { 'Content-Type': 'application/json' },
    body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    ...init,
  });

  if (!res.ok) {
    let detail = res.statusText;
    let code: string | undefined;
    try {
      const data = await res.json();
      detail = data.detail || detail;
      code = data.code;
    } catch {
      /* response had no JSON body */
    }
    const err: ApiError = { status: res.status, detail, code };
    throw err;
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  postForm: <T>(path: string, body: FormData) => request<T>('POST', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

// ── Types ──────────────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  name: string;
  picture_url?: string;
  plan: 'free' | 'pro';
  plan_kind?: 'free' | 'monthly' | 'annual' | 'lifetime';
  rights_acknowledged_at: string | null;
  download_acknowledged_at: string | null;
  created_at: string;
  // Sign-in method state, sent by /api/user/me. `email_verified` is false only
  // for a fresh email+password signup that hasn't followed its confirm link —
  // Google accounts are verified by Google, and every account that predates
  // password sign-in was backfilled. It gates ONE thing: starting a checkout.
  // `has_password` is false for Google-only accounts.
  email_verified?: boolean;
  has_password?: boolean;
}

export interface UsageSummary {
  today: number;
  this_month: number;
  daily_quota: number | null; // null = unlimited (pro)
  remaining?: number | null;  // loops LEFT in the current window (null = unlimited)
  resets_at?: string | null;  // ISO; null when no window is open
  plan: 'free' | 'pro';
}

export interface Section {
  id: string;
  source_kind: 'url' | 'upload';
  video_id: string;
  label: string;
  title?: string; // source video title (e.g. YouTube), captured by the extension
  stem: string;
  start: number;
  end: number;
  stem_mix: Record<string, number> | null;
  loop: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserSettings {
  fetch_method: 'backend' | 'client';
  audio_quality: 'fast' | 'standard' | 'high';
  mixer_open: boolean;
}

export interface JobStartResponse {
  job_id: string;
}

export interface JobStatus {
  step: string;
  error?: string;
  stem_urls?: Record<string, string>;
  stems_cache_id?: string;
  stem_energy?: Record<string, number>;
  cached?: boolean;
  padded_seconds?: number;
  // fetch-audio jobs only: where to download the fetched track + what it is.
  audio_url?: string | null;
  title?: string | null;
  duration?: number | null;
  video_id?: string | null;
  source_url?: string | null;
}

// ── Endpoints ──────────────────────────────────────────────────────────

// Where the /extension-auth handoff may bounce to. Mirrors ext_redirect_ok()
// in the PHP backend — keep the two in step.
//   • https://<id>.chromiumapp.org/ — a Chrome extension's identity URL; it
//     receives the bearer token in the fragment (unchanged behaviour).
//   • riffloop://auth and <applicationId>://auth — the phone apps. Neither ever
//     receives the token: they get a one-time PKCE-bound code (ExtensionAuth.tsx).
export const APP_REDIRECT = 'riffloop://auth';
// Every Android build used to declare riffloop://auth, so two builds on one phone fought
// over the sign-in redirect and the loser was left signed out. From 20-09-26 each build
// uses its own applicationId as the scheme (app.riffloop.studio://auth, and .demo / .yt /
// .yt.demo), so the family is accepted instead of a list that grows per build id.
// riffloop://auth stays: shipped Android builds and the iPhone app still use it.
const APP_REDIRECT_RE = /^app\.riffloop\.studio(\.[a-z0-9]+)*:\/\/auth$/i;
export function isAppRedirect(s: string): boolean {
  return s === APP_REDIRECT || APP_REDIRECT_RE.test(s);
}
export function extRedirectOk(s: string): boolean {
  return isAppRedirect(s) || /^https:\/\/[a-z0-9-]+\.chromiumapp\.org\//i.test(s);
}

// Where a provider button sends the browser: the backend's /start for that provider, which redirects
// to Google or Apple with a fresh state, back to its callback, then to `next` (if given — e.g. resume
// a /checkout intent) or home, with the session cookie set. `next` is restricted to same-origin paths
// so a crafted value can't turn login into an open redirect.
function signInStartUrl(start: string, next?: string, extRedirect?: string): string {
  const parts: string[] = [];
  if (next && next.startsWith('/') && !next.startsWith('//')) {
    parts.push('next=' + encodeURIComponent(next));
  }
  // Extension sign-in only: the backend stashes this so an OAuth FAILURE can
  // bounce back to the extension's chromiumapp.org redirect. Without it the
  // popup would park on an error page instead of closing, because
  // launchWebAuthFlow only resolves on a nav to chromiumapp.org. The backend
  // re-validates the host, so a crafted value can't exfiltrate anything.
  if (extRedirect && extRedirectOk(extRedirect)) {
    parts.push('ext_redirect=' + encodeURIComponent(extRedirect));
  }
  return start + (parts.length ? '?' + parts.join('&') : '');
}

export const auth = {
  me: () => api.get<User | null>('/api/user/me'),
  ackRights: () => api.post<{ rights_acknowledged_at: string }>('/api/user/acknowledge-rights'),
  ackDownload: () => api.post<{ download_acknowledged_at: string }>('/api/user/acknowledge-download'),
  logout: () => api.post<void>('/api/auth/logout'),
  startGoogle: (next?: string, extRedirect?: string) => {
    window.location.href = signInStartUrl('/api/auth/google/start', next, extRedirect);
  },
  // Sign in with Apple on the website. Only offered when providers() says so: the backend keeps it
  // switched off until Apple's Services ID is configured, and an older backend has no such route.
  startApple: (next?: string, extRedirect?: string) => {
    window.location.href = signInStartUrl('/api/auth/apple/start', next, extRedirect);
  },
  providers: () => api.get<{ apple?: boolean }>('/api/auth/providers'),
  deleteAccount: () => api.delete<void>('/api/account'),

  // ── Email + password ─────────────────────────────────────────────────────
  // All four set the same session cookie the Google flow does, so callers just
  // refresh() the auth context afterwards. Errors carry a `code` the forms
  // branch on: 'email_taken' | 'weak_password' | 'bad_email' |
  // 'bad_credentials' | 'bad_token' | 'rate_limited'.
  // Activation-first: this creates the account but does NOT sign anyone in.
  // The caller must show a "check your inbox" state; the session only starts
  // when the emailed link is followed.
  signup: (email: string, password: string) =>
    api.post<{ pending: true; email: string }>('/api/auth/signup', { email, password }),
  login: (email: string, password: string) =>
    api.post<User>('/api/auth/login', { email, password }),
  // Always resolves, whether or not the address has an account — the backend
  // deliberately gives the same answer either way so this can't be used to test
  // which addresses are registered. Never branch the UI on the result.
  forgotPassword: (email: string) =>
    api.post<{ ok: true }>('/api/auth/password/forgot', { email }),
  resetPassword: (token: string, password: string) =>
    api.post<User>('/api/auth/password/reset', { token, password }),
  sendVerification: () =>
    api.post<{ ok: true; already?: boolean }>('/api/auth/verify/send'),
  // Correct a mistyped address. Backend refuses once the address is confirmed
  // ('already_verified') or if billing is attached ('has_plan'); it also sends a
  // fresh link to the new address and kills the one mailed to the old one.
  changeEmail: (email: string) =>
    api.post<{ ok: true; email: string }>('/api/auth/email/change', { email }),
};

export const usage = {
  me: () => api.get<UsageSummary>('/api/usage/me'),
};

export const fetchAudio = {
  // Server-side YouTube audio fetch for the Studio's "From link" tab. The
  // endpoint sits behind the same rights gate as /api/separate: signed-in
  // users are checked against the DB, anonymous users must send the
  // X-Rights-Acknowledged header (set from localStorage after the modal).
  start: (url: string, rightsAckLocally: boolean) =>
    request<JobStartResponse>('POST', '/api/fetch-audio', { url }, {
      headers: {
        'Content-Type': 'application/json',
        ...(rightsAckLocally ? { 'X-Rights-Acknowledged': 'true' } : {}),
      },
    }),
  status: (jobId: string) => api.get<JobStatus>(`/api/status/${encodeURIComponent(jobId)}`),
};

export const sections = {
  list: () => api.get<Section[]>('/api/sections/me'),
  // The server caps brand-NEW loops per song on the free plan and answers 200 with
  // `blocked` rather than an error — so callers MUST read it, or a refused save
  // looks exactly like a successful one.
  sync: (rows: Partial<Section>[]) =>
    api.post<{ saved: number; blocked?: number; cap?: number }>('/api/sections/sync', { sections: rows }),
  remove: (id: string) => api.delete<void>(`/api/sections/${id}`),
};

// Fire-and-forget event log for a FRESH separation. Lets the owner admin show
// real per-user activity (separation itself runs client-side, so the server
// otherwise can't count it). Best-effort: any failure is swallowed so a logging
// hiccup never affects the Studio.
export const separations = {
  log: (ev: { video_id?: string; source_kind?: string; stem?: string; surface?: string; source_url?: string; quality?: string }) =>
    api.post<{ ok: boolean }>('/api/separations/log', ev).catch(() => {}),
};

export interface ConsumeResult {
  ok: boolean;
  plan: string;            // 'free' | 'pro' | 'anonymous'
  limit: number | null;
  used?: number;
  remaining: number | null;
  exhausted: boolean;
  resets_at: string | null;
}
export const loops = {
  // Quota gate: atomically counts a loop against the account's 24h window and
  // records it. Returns exhausted:true (no count) when the cap is hit. Also the
  // single source of the separations activity log — callers must NOT also call
  // separations.log (that would double-count admin activity).
  consume: (ev: { video_id?: string; source_kind?: string; stem?: string; surface?: string; source_url?: string; quality?: string }) =>
    api.post<ConsumeResult>('/api/loops/consume', ev),
};

export const settings = {
  get: () => api.get<UserSettings>('/api/user/settings'),
  update: (patch: Partial<UserSettings>) => api.post<UserSettings>('/api/user/settings', patch),
};

export const billing = {
  checkoutSession: (plan: 'monthly' | 'annual' | 'lifetime' | 'onetime' = 'monthly') =>
    api.post<{ url: string }>('/api/billing/checkout-session', { plan }),
  // Everything the Billing page renders, from our own mirror of the plan.
  summary: () => api.get<BillingSummary>('/api/billing/summary'),
  // One-click cancel. The subscription is identified server-side from the
  // session — deliberately NOT a parameter here (the PayPro store is shared
  // with other live apps, so a client-supplied id must never reach it).
  cancel: (reason?: string) =>
    api.post<{ ok: true; already?: boolean; pro_until: string | null }>('/api/billing/cancel', {
      reason: reason ?? '',
    }),
};

// `state` is computed server-side so the client holds no plan logic:
//   free · active · ending (cancelled, paid time left) · ended · lifetime
export interface BillingSummary {
  state: 'free' | 'active' | 'ending' | 'ended' | 'lifetime';
  plan: 'free' | 'pro';
  plan_kind: 'free' | 'monthly' | 'annual' | 'lifetime';
  billing_email: string;
  pro_since: string | null;
  // Omitted (null) whenever unknown — the UI drops the row rather than
  // rendering a placeholder. Only populates from a charge after this shipped.
  renews_on: string | null;
  renew_amount: string | null;
  last_payment: { date: string | null; amount: string; currency: string } | null;
  order_id: string | null;
  pro_until: string | null;
  canceled_at: string | null;
  can_cancel: boolean;
}

// The backend middleware (riffloop_middleware.py) requires either a logged-in
// user with rights_acknowledged_at set, OR the X-Rights-Acknowledged header
// for anonymous callers. We read the flag from localStorage that the rights
// modal set after the user accepted.
function rightsHeader(): Record<string, string> {
  try {
    if (localStorage.getItem('riffloop_rights_acknowledged_at')) {
      return { 'X-Rights-Acknowledged': 'true' };
    }
  } catch {
    /* no localStorage — server will reject and the modal will pop again */
  }
  return {};
}

async function separateRequest<T>(path: string, body: BodyInit | FormData, isJson: boolean): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...(isJson ? { 'Content-Type': 'application/json' } : {}),
      ...rightsHeader(),
    },
    body,
  });
  if (!res.ok) {
    let detail = res.statusText;
    let code: string | undefined;
    try {
      const data = await res.json();
      detail = data.detail || detail;
      code = data.code;
    } catch {
      /* no JSON body */
    }
    throw { status: res.status, detail, code } as ApiError;
  }
  return res.json();
}

// (Mix/stem export is now fully client-side — see lib/audioExport.ts. The old
//  server `/api/export-mix` path is obsolete and was removed.)

export const separation = {
  fromUrl: (req: { url: string; start: number; end: number; quality: string }) =>
    separateRequest<JobStartResponse>('/api/separate', JSON.stringify(req), true),
  fromUpload: (file: File, opts: { url: string; start: number; end: number; quality: string }) => {
    const fd = new FormData();
    fd.append('audio', file);
    fd.append('url', opts.url);
    fd.append('start', String(opts.start));
    fd.append('end', String(opts.end));
    fd.append('quality', opts.quality);
    return separateRequest<JobStartResponse>('/api/separate-upload', fd, false);
  },
  status: (jobId: string) => api.get<JobStatus>(`/api/status/${jobId}`),
};
